/**
 * The Guide's contents list, its search, and the "where am I" calculation.
 *
 * WHY THIS IS A SEPARATE FILE FROM `Guide.tsx`
 *
 * There is no browser test harness in this project — no jsdom, no
 * testing-library — so anything that only exists once React has rendered cannot
 * be tested at all. Everything below is therefore a plain function over plain
 * data: the parser, the ids, the hit counting, the highlighting, and the
 * arithmetic that decides which section you are currently looking at. The
 * component keeps the parts that genuinely need a browser — scroll positions,
 * a stored preference, an effect — and nothing else.
 *
 * WHY A CONTENTS LIST EXISTS AT ALL
 *
 * The guide is long enough that reading it top to bottom is the only way
 * through it, and nobody does that. Renee will arrive with one question, and
 * the honest answer to "how do I clear the mailing list" cannot be "scroll".
 *
 * No section count is quoted here, and that is deliberate rather than lazy. An
 * earlier version of this comment said 21 sections and 19 subsections; the
 * document had already grown past both. `test-guidetoc.mjs` asserts the count
 * against the real text, which is the only place a number like that can live
 * without going quietly stale.
 *
 * WHY A SEARCH BOX WHEN Ctrl+F EXISTS
 *
 * Ctrl+F finds words. What it cannot say is which SECTION they are in, and on a
 * page this long that is the entire question — the value here is the map, not
 * the finding. So the search filters the contents list and counts hits per
 * section, and deliberately does NOT hide anything from the page itself. A
 * search that hid text would answer "where is this mentioned" by destroying the
 * context that makes the mention mean anything.
 */

export type Block =
  | { kind: 'h1' | 'h2' | 'h3' | 'p'; text: string }
  | { kind: 'ul'; items: string[] }

/**
 * The guide's grammar, in full: headings, bullets, paragraphs and **bold**.
 *
 * Moved here from `Guide.tsx` unchanged in behaviour, so that the thing the
 * contents list is built out of is the same thing the page is built out of. Two
 * parsers would drift, and the failure would be a contents entry pointing at a
 * heading that no longer exists.
 */
export function parse(source: string): Block[] {
  const out: Block[] = []
  let para: string[] = []
  let list: string[] = []

  // A paragraph in the source may be wrapped across several lines; joining them
  // is what stops the rendered page breaking mid-sentence at the source's
  // 80-column margin.
  const flushPara = () => {
    if (para.length) out.push({ kind: 'p', text: para.join(' ') })
    para = []
  }
  const flushList = () => {
    if (list.length) out.push({ kind: 'ul', items: list })
    list = []
  }
  const flush = () => {
    flushPara()
    flushList()
  }

  for (const raw of source.split('\n')) {
    const line = raw.trimEnd()

    if (line.trim() === '') {
      flush()
      continue
    }

    if (line.startsWith('### ')) {
      flush()
      out.push({ kind: 'h3', text: line.slice(4) })
    } else if (line.startsWith('## ')) {
      flush()
      out.push({ kind: 'h2', text: line.slice(3) })
    } else if (line.startsWith('# ')) {
      flush()
      out.push({ kind: 'h1', text: line.slice(2) })
    } else if (line.startsWith('- ')) {
      flushPara()
      list.push(line.slice(2))
    } else if (list.length && line.startsWith('  ')) {
      // Continuation of the bullet above, wrapped in the source.
      list[list.length - 1] += ' ' + line.trim()
    } else {
      flushList()
      para.push(line.trim())
    }
  }

  flush()
  return out
}

/**
 * A block chopped into exactly the runs of text the PAGE draws.
 *
 * THIS IS THE FIX FOR THE WORST DEFECT THIS FEATURE HAD, and it is worth
 * understanding before anybody simplifies it back.
 *
 * The renderer splits on `**` and highlights inside each run separately — it
 * has to, because the bold marker is not text and cannot be highlighted. The
 * counter used to do the opposite: strip the markers, glue the runs together,
 * and count in the result. So any query straddling a bold boundary was counted
 * and could never be highlighted.
 *
 * That is not a corner case in this document. The guide is full of bold UI
 * labels mid-sentence — *"a **More filters** button, a **Clear filters**
 * button"* — and typing three words you can see on screen is the most natural
 * thing a reader does. Renee gets *"1 mention in 1 section. They are
 * highlighted below."*, clicks through, and hunts for a marker that does not
 * exist. Of the adjacent word pairs in the real guide that span a bold marker,
 * **317 of 386** produced a count with no highlight.
 *
 * Bullets are separate runs for the same reason: each is its own `<li>`, so a
 * query can no more span two bullets than it can span a bold marker. That also
 * subsumes the earlier newline-join fix — the seam is gone rather than made
 * unmatchable.
 *
 * **If you change how the page draws text, change this in the same commit.**
 * The count and the highlight must be computed from the same runs or the number
 * on screen becomes a claim the page cannot honour.
 */
export function runs(b: Block): string[] {
  const raw = b.kind === 'ul' ? b.items : [b.text]
  return raw.flatMap((t) => t.split('**'))
}


/**
 * A heading turned into something that can live in a URL.
 *
 * These end up in the address bar — `/guide#the-mailing-list` — so they are
 * built from the heading's own words rather than from its position. A
 * positional id (`#section-7`) would point somewhere else the moment a section
 * was inserted above it, silently, and every link anybody had saved would land
 * on the wrong text while still looking like it worked.
 *
 * Apostrophes are dropped rather than replaced, so "What's Missing" is
 * `whats-missing` and not `what-s-missing`.
 */
export function slug(text: string): string {
  const base = text
    .split('**')
    .join('')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return base || 'section'
}

export type Heading = {
  id: string
  text: string
  level: 2 | 3
  /** Where this heading sits in the block list. */
  at: number
}

/**
 * The contents list: every `##` and `###`, in the order they appear.
 *
 * `#` is left out on purpose — it is the document's title, and a contents list
 * whose first entry is the name of the thing you are already reading is one
 * wasted line at the top of every screen.
 *
 * IDS ARE MADE UNIQUE HERE, and that is not hypothetical tidiness. Two
 * subsections could easily both be called "Clearing it" under different
 * parents; duplicate ids would make `#clearing-it` jump to whichever came
 * first, and the scroll-spy would light up the wrong entry for one of them
 * forever.
 */
export function outline(blocks: Block[]): Heading[] {
  /**
   * Every id ever emitted, not a count per base.
   *
   * Counting per base was not enough, and the way it failed is nastier than it
   * looks. Three headings — "Clearing it", "Clearing it 2", "Clearing it" —
   * produce `clearing-it`, `clearing-it-2`, and then `clearing-it-2` AGAIN,
   * because the suffixed candidate was never checked against the ids other
   * headings had already taken. Two contents entries then jump to the same
   * heading, React gets duplicate keys, and their hit counts merge into one
   * bucket. It survived review once because no heading in the guide currently
   * ends in a digit — the assertion passed on the data, not on the code.
   */
  const taken = new Set<string>()
  const out: Heading[] = []
  blocks.forEach((b, at) => {
    if (b.kind !== 'h2' && b.kind !== 'h3') return
    const base = slug(b.text)
    let id = base
    let n = 1
    while (taken.has(id)) {
      n++
      id = `${base}-${n}`
    }
    taken.add(id)
    out.push({
      id,
      text: b.text.split('**').join(''),
      level: b.kind === 'h2' ? 2 : 3,
      at,
    })
  })
  return out
}

/**
 * The shortest thing worth searching for.
 *
 * Two letters match somewhere in nearly every section of a 21,000-pixel
 * document, so a contents list "filtered" by them is the unfiltered list with a
 * misleading set of numbers beside it. Below the floor the search is simply not
 * on, which is why this returns an empty string rather than a short one.
 */
export const MIN_QUERY = 3

export function normalizeQuery(q: string): string {
  const t = q.trim().toLowerCase()
  return t.length >= MIN_QUERY ? t : ''
}

/**
 * Non-overlapping, case-insensitive occurrences of `query` in `text`.
 *
 * BOTH sides are lowered. Lowering only the haystack made this
 * case-insensitive in one direction — `countIn('Tag and TAG', 'TAG')` returned
 * zero — with the invariant living outside the function, in whichever caller
 * happened to have run the query through `normalizeQuery` first. An exported
 * function whose correctness depends on a convention its callers remember is a
 * defect waiting for its second caller.
 */
export function countIn(text: string, query: string): number {
  const needle = query.toLowerCase()
  if (!needle) return 0
  const hay = text.toLowerCase()
  let n = 0
  let i = hay.indexOf(needle)
  while (i !== -1) {
    n++
    i = hay.indexOf(needle, i + needle.length)
  }
  return n
}

export type Hits = {
  /** Matches in the whole document, headings included. */
  total: number
  /**
   * Matches per heading id — a `##` entry INCLUDES the matches inside its own
   * `###` subsections.
   *
   * Without that, searching for a word that only appears in a subsection leaves
   * the parent showing 0 while its child shows 4, which reads as a bug in the
   * counting rather than as the shape of the document.
   */
  byId: Record<string, number>
  /** Ids with at least one match, in document order. */
  ids: string[]
}

export function countHits(blocks: Block[], headings: Heading[], query: string): Hits {
  const byId: Record<string, number> = {}
  for (const h of headings) byId[h.id] = 0
  if (!query) return { total: 0, byId, ids: [] }

  // Indexed by position rather than searched for. This runs on every keystroke,
  // and a scan of the headings inside a scan of the blocks is a thousand-fold
  // more work for the same answer.
  const at = new Map<number, Heading>()
  for (const h of headings) at.set(h.at, h)

  /**
   * Blocks before the first heading — the title and its opening lines — belong
   * to no section. Their matches count towards the total and towards nothing
   * else, which is honest: there is no contents entry to send anybody to.
   */
  let currentId: string | null = null
  let parentId: string | null = null
  let total = 0

  const credit = (n: number) => {
    total += n
    if (currentId) byId[currentId] += n
    if (parentId && parentId !== currentId) byId[parentId] += n
  }

  /**
   * One code path for headings and body alike, and both counted over `runs`.
   *
   * The heading branch used to count against `Heading.text`, which has the bold
   * markers stripped and glued — the same defect as the body had, reached by a
   * second route. A heading with a bold word in it would have counted a match
   * across the join that the page could never highlight.
   */
  const tally = (b: Block) => runs(b).reduce((sum, r) => sum + countIn(r, query), 0)

  blocks.forEach((b, i) => {
    const heading = at.get(i)
    if (heading) {
      currentId = heading.id
      // An `###` keeps the `##` above it as its parent; an `##` becomes its own.
      parentId = heading.level === 2 ? heading.id : parentId
    }
    credit(tally(b))
  })

  return { total, byId, ids: headings.filter((h) => byId[h.id] > 0).map((h) => h.id) }
}

export type Piece = { text: string; hit: boolean }

/**
 * Text split into matched and unmatched runs, for highlighting.
 *
 * A plain string scan rather than a regular expression, because the needle is
 * whatever Renee typed. `What's Missing (` compiles to an unterminated group
 * and throws; `.` matches everything and lights the page up like a Christmas
 * tree. Neither is a thing a search box should be capable of.
 */
export function pieces(text: string, query: string): Piece[] {
  const needle = query.toLowerCase()
  if (!needle) return [{ text, hit: false }]
  const hay = text.toLowerCase()
  /**
   * Offsets are found in the lowered copy and applied to the original, which is
   * only sound while lowering preserves length. It does not always: `İ`
   * lowercases to two code units, and every highlight after it in that string
   * lands one character to the right.
   *
   * Highlighting nothing is the right failure here. The text still renders in
   * full and the contents list still reports the section — she loses the yellow
   * marker, not the answer. A round-trip check cannot catch this, because a
   * mis-split still reassembles into the original string.
   */
  if (hay.length !== text.length) return [{ text, hit: false }]
  const out: Piece[] = []
  let from = 0
  let i = hay.indexOf(needle)
  while (i !== -1) {
    if (i > from) out.push({ text: text.slice(from, i), hit: false })
    out.push({ text: text.slice(i, i + needle.length), hit: true })
    from = i + needle.length
    i = hay.indexOf(needle, from)
  }
  if (from < text.length) out.push({ text: text.slice(from), hit: false })
  return out
}

/**
 * Which section the reader is currently in.
 *
 * The last heading that has gone past the top of the window, allowing a little
 * slack — `offset` — so that a heading sitting just under the top edge counts
 * as the one you are reading rather than the one before it.
 *
 * `atBottom` exists because the last section of a long document is usually
 * short. Scrolled all the way down, its heading can still be below the top of
 * the window, so the arithmetic alone marks the second-to-last entry and the
 * final section can never be reached — the reader is looking straight at it
 * while the contents list points somewhere else.
 *
 * Null before the first heading has passed. Marking section one while the
 * reader is still on the title would be a small lie told constantly.
 */
export function currentHeadingId(
  tops: readonly { id: string; top: number }[],
  scrollY: number,
  offset: number,
  atBottom = false,
): string | null {
  if (tops.length === 0) return null
  if (atBottom) return tops[tops.length - 1].id
  const line = scrollY + offset
  let found: string | null = null
  for (const t of tops) {
    if (t.top <= line) found = t.id
    else break
  }
  return found
}

/**
 * Reading sizes, as multipliers on every text size in the guide.
 *
 * Steps rather than a slider: five named sizes somebody can get back to, versus
 * a continuum where "a bit bigger than last time" is unrepeatable. The first is
 * exactly today's size, so the control can never make the page look wrong by
 * simply existing.
 */
export const READING_SIZES = [1, 1.15, 1.3, 1.5, 1.75] as const
export const DEFAULT_SIZE_STEP = 0

/** Kept in range, so a stored value from a future or corrupted build cannot
 *  render the guide at 40 times its size or at zero. */
export function clampStep(step: number): number {
  if (!Number.isFinite(step)) return DEFAULT_SIZE_STEP
  const i = Math.round(step)
  if (i < 0) return 0
  if (i > READING_SIZES.length - 1) return READING_SIZES.length - 1
  return i
}

/**
 * Which part of the guide explains the screen you are standing on.
 *
 * WHY THIS IS A MAP AND NOT A PROPERTY OF EACH SCREEN
 *
 * The pairing between a screen and its explanation is the sort of thing that
 * rots silently: a section gets renamed, the id changes, and a help link starts
 * landing at the top of the page instead of the paragraph it promised. Keeping
 * every pair in one place is what makes it possible to assert — in
 * `test-guidetoc.mjs`, against the real guide text — that every id here names a
 * heading that exists AND that it names the right one. A property scattered
 * across the route components could not be checked at all.
 *
 * The four agent lists share an entry. They are the same screen with different
 * membership rules, and the guide describes them in one section.
 *
 * `/guide` is deliberately absent: there is no sense in a link from the guide to
 * the guide, and the control hides itself when this returns null.
 */
export const GUIDE_SECTIONS: Record<string, string> = {
  '/': 'the-agent-tabs',
  '/capped': 'the-agent-tabs',
  '/elite': 'the-agent-tabs',
  '/leaders': 'the-agent-tabs',
  '/outreach': 'the-outreach-tab',
  '/outbox': 'the-outbox-tab',
  '/bulk': 'the-bulk-tab',
  '/drips': 'drips',
  '/scheduled': 'the-scheduled-sends-tab',
  '/templates': 'templates',
  '/triggers': 'triggers',
  '/missing': 'whats-missing',
  '/tasks': 'tasks',
  '/bounces': 'bounces',
  '/requests': 'the-requests-tab',
  '/pearl': 'the-pearl-tab',
  // Reached from the toolbar button rather than a tab, which makes having an
  // answer for it matter more, not less — there is no tab to point at.
  '/email': 'emailing-a-group',
}

/**
 * The section for a path, or null where there is nothing useful to offer.
 *
 * The trailing slash is stripped first for the same reason `App` strips it:
 * react-router matches `/capped/` to the `/capped` route, so a raw lookup would
 * miss and the help control would vanish on a URL that renders a screen
 * perfectly well.
 */
export function sectionForPath(path: string): string | null {
  const clean = path.replace(/\/+$/, '') || '/'
  return GUIDE_SECTIONS[clean] ?? null
}
